import RunVisualizer from '@automata/run-visualizer';
import { RunProgress } from '@automata/run-visualizer/src/types';
import { ErrorDisplay, Spinner } from '@automata/ui';
import { logger } from '@automata/utils';
import { GetPlanResultError } from '@automata/wfb-api';
import { WorkflowSaveQueryError } from '@automata/wfb-api/src/useSaveWorkflow/types';
import { WorkflowPlanError } from '@automata/wfb-api/src/useWorkflowPlan/types';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';

import { MaybeWorkflowConfig } from '../../features/workflowJsonSelectors';
import { toPreviewBlockers } from '../../utils/previewBlockers';
import AwaitingPreviewDisplay from '../AwaitingPreviewDisplay';
import EmptyPreviewDisplay from '../EmptyPreviewDisplay';
import PreviewBlockedDisplay from '../PreviewBlockedDisplay';
import PreviewCancelledDisplay from '../PreviewCancelledDisplay';
import PreviewPlanningFailedDisplay from '../PreviewPlanningFailedDisplay';

type ErrorWithType = WorkflowPlanError | WorkflowSaveQueryError;

const getFailedPlanErrorMessage = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') return null;

  const record = value as Record<string, unknown>;

  if ('status' in record && record.status === 'FAILED') {
    return typeof record.error === 'string' ? record.error : 'Unknown error';
  }

  if ('error' in record) {
    return getFailedPlanErrorMessage(record.error);
  }

  return null;
};

const hasErrorType = (
  error: Props['fetchErrors'][number]
): error is ErrorWithType =>
  typeof error === 'object' && error !== null && 'errorType' in error;

const isWorkflowPlanError = (
  error: Props['fetchErrors'][number]
): error is WorkflowPlanError =>
  hasErrorType(error) && error.errorType === 'pollWorkflowPlanStatus';

const getPlanningFailedErrorMessage = (
  fetchErrors: Props['fetchErrors']
): string | null => {
  const planningError = fetchErrors.find(isWorkflowPlanError);

  return planningError ? getFailedPlanErrorMessage(planningError.error) : null;
};

export type Props = {
  fetchErrors: Array<
    WorkflowPlanError | WorkflowSaveQueryError | GetPlanResultError
  >;
  isLoading: boolean;
  previewState: 'blocked' | 'empty' | 'loading' | 'ready' | 'cancelled';
  runProgressData: RunProgress | null; // TODO(LT, 16/07/2024): Can this be determined via type narrowing?
  workflowJson: MaybeWorkflowConfig;
  onClickReturnToWorkflow: () => void;
  planId: string;
  stageDetail?: string | null;
  stoppedEarly?: boolean;
};

const Content = ({
  fetchErrors,
  isLoading,
  previewState,
  runProgressData,
  workflowJson,
  onClickReturnToWorkflow,
  planId,
  stageDetail,
  stoppedEarly,
}: Props): JSX.Element | null => {
  if (isLoading) return <Spinner />;

  const planningFailedErrorMessage = getPlanningFailedErrorMessage(fetchErrors);
  if (planningFailedErrorMessage) {
    logger.error(fetchErrors);
    return (
      <PreviewPlanningFailedDisplay
        errorMessage={planningFailedErrorMessage}
        onClickReturnToWorkflow={onClickReturnToWorkflow}
      />
    );
  }

  if (fetchErrors.length) {
    logger.error(fetchErrors);
    return (
      <ErrorDisplay
        error={fetchErrors[0]}
        userMessage="Failed to load data"
        logToConsole
      />
    );
  }

  if (previewState === 'blocked') {
    return (
      <PreviewBlockedDisplay
        previewBlockers={toPreviewBlockers(workflowJson)}
        onClickReturnToWorkflow={onClickReturnToWorkflow}
      />
    );
  }

  if (previewState === 'empty') {
    return <EmptyPreviewDisplay />;
  }

  if (previewState === 'loading') {
    return <AwaitingPreviewDisplay planId={planId} stageDetail={stageDetail} />;
  }

  if (previewState === 'cancelled') {
    return (
      <PreviewCancelledDisplay
        onClickReturnToWorkflow={onClickReturnToWorkflow}
      />
    );
  }

  if (!runProgressData) return null;

  // ensure all union cases covered
  previewState satisfies 'ready';

  return (
    <Stack p={3}>
      {stoppedEarly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This preview was stopped early. The result shown is a partial plan
          based on progress made before cancellation.
        </Alert>
      )}
      <RunVisualizer runProgressData={runProgressData} />
    </Stack>
  );
};

export default Content;
